import React from "react";
import {
  Outlet,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { MessageSquareText } from "lucide-react";
import { useIsRestoring, useQueryClient } from "@tanstack/react-query";
import { useMeQuery } from "@/queries/queries";
import PortalNav from "./PortalNav";
import PortalMobileNav from "./PortalMobileNav";
import TrackerSidebar, { isPortalDashboardPath } from "./TrackerSidebar";
import AddBuildingDialog from "./AddBuildingDialog";
import { AddBuildingProvider, useAddBuilding } from "./useAddBuilding";
import { usePortalBuildingBins } from "@/queries/portalQueries";
import { loginRedirect } from "@/lib/postLoginHome";
import { useMailLanding } from "./useMailLanding";
import { activatePortalTheme } from "./portalTheme";
import { hasPortalPageData, usePortalRevalidate } from "./usePortalRevalidate";
import DashboardFooter from "@/pages/Portal/Dashboard/DashboardFooter";
import { FadeIn, useDelayedFlag, useResolvedPathname } from "./motion";
import { CHAT_ENABLED } from "./chatEnabled";
import {
  NOT_ON_TRACKER_COPY,
  usePortalFeaturesQuery,
} from "./usePortalFeatures";
import "./motion/motion.css";

/**
 * Filing-tier chat affordance. Wave-10 (2026-05-24) killed the drawer chat, so
 * this navigates to `/portal/chat`. `bg-ink` per Leo's "no emerald anywhere".
 * Non-Tracker users only; Tracker users get chat-as-hero in TrackerSidebar.
 */
const AskClearFab: React.FC = () => {
  const navigate = useNavigate();
  const open = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    navigate({ to: "/portal/chat" });
  };
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Ask Clear"
      className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-ink text-paper shadow-lg ring-1 ring-ink/10 hover:bg-ink/90 motion-tint flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-line-strong focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
      data-testid="portal-ask-clear-fab"
    >
      <MessageSquareText className="h-5 w-5" aria-hidden="true" />
    </button>
  );
};

/**
 * Mounted inside the AddBuildingProvider so the consumer hook works. BINs come
 * from ``/tracking/portal/building-bins/`` for the duplicate-add dedupe:
 * Wave-15 Track A split that off the dashboard payload so a 321-building
 * header poll does not ship every row's metrics for the BIN set.
 *
 * Exported so Dashboard Page.test.tsx mounts the real dialog: one source of
 * truth for existingBins and prefill (DRY-across-surfaces).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const GlobalAddBuildingDialog: React.FC = () => {
  const { open, setOpen, prefill } = useAddBuilding();
  const { data } = usePortalBuildingBins();
  const existingBins = React.useMemo(() => {
    const set = new Set<string>();
    for (const bin of data?.bins ?? []) {
      if (bin) set.add(bin);
    }
    return set;
  }, [data?.bins]);
  // A prefill BIN (Phase 3.A, the "Confirm details to estimate" CTA) is
  // already in the portfolio; strip it or the dedupe guard blocks Step 2.
  const effectiveExistingBins = React.useMemo(() => {
    if (!prefill?.bin) return existingBins;
    const set = new Set(existingBins);
    set.delete(prefill.bin);
    return set;
  }, [existingBins, prefill?.bin]);
  return (
    <AddBuildingDialog
      open={open}
      onOpenChange={setOpen}
      existingBins={effectiveExistingBins}
      prefill={prefill ?? undefined}
    />
  );
};

/**
 * The page fades and its h1 takes focus on PUSH and REPLACE (a beforeLoad
 * redirect, /portal/team to Settings, arrives as REPLACE). History moves
 * keep the browser's scroll and focus.
 */
const HISTORY_MOVES = new Set(["BACK", "FORWARD", "GO"]);

/** `fill` (non-chat routes): a screen above the footer at all times, so a landing page never moves it (load CLS 0.024 to 0, 11-01); short pages scroll 43 px. */
const RouteOutlet: React.FC<{ fill: boolean }> = ({ fill }) => {
  const pathname = useResolvedPathname();
  const router = useRouter();
  const lastAction = React.useRef<string>("PUSH");
  React.useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        lastAction.current = action.type;
      }),
    [router],
  );
  const ref = React.useRef<HTMLDivElement>(null);
  const isHistoryMove = HISTORY_MOVES.has(lastAction.current);
  const focusedFor = React.useRef(pathname);
  React.useEffect(() => {
    if (focusedFor.current === pathname) return undefined;
    focusedFor.current = pathname;
    if (HISTORY_MOVES.has(lastAction.current)) return undefined;
    const root = ref.current;
    if (!root) return undefined;
    // Child effects run first: a hash target the page already focused (charge-row jump) keeps it.
    const active = document.activeElement;
    if (window.location.hash && active !== root && root.contains(active))
      return undefined;
    const focus = (el: HTMLElement) => {
      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
      el.setAttribute("data-route-focus", "");
      el.focus({ preventScroll: true });
    };
    focus(root);
    // Data-gated pages draw their h1 late; hand focus on unless the user moved it.
    const toH1 = () => {
      const h1 = root.querySelector<HTMLElement>("h1");
      if (!h1 || document.activeElement !== root) return false;
      focus(h1);
      return true;
    };
    if (toH1()) return undefined;
    const observer = new MutationObserver(() => {
      if (toH1() || document.activeElement !== root) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });
    const stop = window.setTimeout(() => observer.disconnect(), 3000);
    return () => {
      observer.disconnect();
      window.clearTimeout(stop);
    };
  }, [pathname]);
  return (
    <FadeIn
      ref={ref}
      replayOn={pathname}
      skip={isHistoryMove}
      speed="fast"
      className={`flex min-w-0 flex-1 flex-col${fill ? " min-h-screen" : ""}`}
    >
      <Outlet />
    </FadeIn>
  );
};

// Account pages and chat still serve a company that is off the Tracker.
const OFF_TRACKER_PAGES = ["/portal/chat", "/portal/settings", "/portal/team"];

/** Leo Option A (10-20): the one empty state when the company is not pinned IN on Ingestion Scope. */
const NotOnTracker: React.FC<{ isStaff: boolean }> = ({ isStaff }) => (
  <div
    className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-16"
    data-testid="portal-not-on-tracker"
  >
    <h1 className="text-lg text-ink">{NOT_ON_TRACKER_COPY}</h1>
    {isStaff && (
      <p
        className="text-sm text-ink-mute"
        data-testid="portal-not-on-tracker-staff"
      >
        Staff: pin this company IN on the Ingestion Scope admin.
      </p>
    )}
  </div>
);

/** 2px bar while a route loads, only once the load passes 200ms. */
const PendingBar: React.FC = () => {
  const pending = useRouterState({ select: (s) => s.status === "pending" });
  const show = useDelayedFlag(pending);
  if (!show) return null;
  return (
    <FadeIn
      role="progressbar"
      aria-label="Loading page"
      speed="fast"
      className="fixed inset-x-0 top-0 z-[90] h-[2px] bg-[hsl(var(--teal))]"
      data-testid="portal-pending-bar"
    />
  );
};

const PortalLayout: React.FC = () => {
  usePortalRevalidate();
  const { data: user, isLoading, isSuccess } = useMeQuery();
  useMailLanding(user);
  const showMeSpinner = useDelayedFlag(isLoading);
  const navigate = useNavigate();
  // Margin Index (sidebar redesign Option B): the index DOCKS only on the
  // Dashboard at ≥lg, the one route whose content is offset. Elsewhere it
  // OVERLAYS and never reflows, so the wrapper gets no gutter.
  const pathname = useResolvedPathname();
  const trackerDockedRoute = isPortalDashboardPath(pathname);
  // Chat is app-like (its composer pins the bottom) and out of the edit
  // lane, so it opts out of the shell footer.
  const isChatRoute = CHAT_ENABLED && pathname.startsWith("/portal/chat");
  const trackingEnabled = !!(user as { trackingEnabled?: boolean } | undefined)
    ?.trackingEnabled;
  const { data: features, isPending: featuresPending } =
    usePortalFeaturesQuery(trackingEnabled);
  const offTracker =
    features?.onTracker === false &&
    !OFF_TRACKER_PAGES.some((p) => pathname.startsWith(p));
  // Restored page data would flash the old dashboard before on_tracker lands.
  const qc = useQueryClient();
  const isRestoring = useIsRestoring();
  const holdPage =
    trackingEnabled &&
    featuresPending &&
    (isRestoring || hasPortalPageData(qc));

  // Themes the document root for the LIFETIME of the /portal shell (rationale
  // in portalTheme.ts). useLayoutEffect so the first paint is themed; the
  // cleanup strips `.dark` on leaving /portal so Classic is not dragged into
  // dark. Before the early returns, for stable hook order.
  React.useLayoutEffect(() => activatePortalTheme(), []);

  React.useEffect(() => {
    if (isSuccess && !user) {
      // A fallback: __root.tsx sends this bounce first and renders nothing until it lands.
      navigate(loginRedirect(window.location.pathname, window.location.search));
    }
  }, [isSuccess, user, navigate]);

  if (isLoading) {
    return (
      <div className="portal-root flex min-h-screen items-center justify-center bg-paper">
        {showMeSpinner && (
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-line border-t-ink" />
        )}
      </div>
    );
  }

  if (!user) return null;

  // Tracker tier (staff or ``tracking_beta``) gets the Margin-Index shell:
  // TrackerSidebar instead of PortalNav, the 264px gutter only while docked,
  // no FAB (chat IS the surface) and no PortalMobileNav: the index's
  // bookmark-tab and overlay are the same pattern at every viewport.
  // Filing tier keeps PortalNav + 220px gutter + PortalMobileNav + FAB.
  return (
    <AddBuildingProvider>
      <div
        className={`portal-root flex flex-row min-h-screen ${
          trackingEnabled ? "bg-paper" : "bg-paper-panel"
        }`}
      >
        <PendingBar />
        {trackingEnabled ? <TrackerSidebar /> : <PortalNav />}
        <div
          className={`motion-gutter flex min-h-screen w-full min-w-0 flex-col ${
            trackingEnabled
              ? trackerDockedRoute
                ? "lg:pl-[264px]"
                : ""
              : "lg:pl-[220px]"
          }`}
        >
          {!trackingEnabled && <PortalMobileNav />}
          {holdPage ? (
            <div className={`flex-1${isChatRoute ? "" : " min-h-screen"}`} />
          ) : offTracker ? (
            <NotOnTracker isStaff={!!user.isStaff} />
          ) : (
            <RouteOutlet fill={!isChatRoute} />
          )}
          {!isChatRoute && <DashboardFooter />}
        </div>
        {!trackingEnabled && !pathname.startsWith("/portal/chat") && (
          <AskClearFab />
        )}
        <GlobalAddBuildingDialog />
      </div>
    </AddBuildingProvider>
  );
};

export default PortalLayout;
